/**
 * Web-side wrappers around the three category-link callables:
 *
 *   - linkPhaseToBacklogCategory        — append one category link
 *   - unlinkPhaseFromBacklogCategory    — remove one category link
 *   - syncBacklogCategoryNow            — sync one entry or all entries
 *
 * The category analogue of `backlogMilestoneLink.ts`; mirrors the same
 * test-runner-injection pattern so vitest can stub the round-trip without
 * touching `firebase/functions`.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './firebase';

export interface LinkPhaseToBacklogCategoryBody {
  projectId: string;
  phaseId: string;
  backlogProjectKey: string;
  categoryId: number;
}

export type LinkPhaseToBacklogCategoryResult =
  | {
      ok: true;
      category: { id: number; name: string; projectKey: string; projectName: string };
      added: string[];
      unchanged: string[];
      totalIssuesInCategory: number;
      truncated: boolean;
    }
  | {
      ok: false;
      reason:
        | 'config-missing'
        | 'secret-error'
        | 'backlog-error'
        | 'phase-not-found'
        | 'category-not-found'
        | 'invalid-project-key';
      message: string;
    };

export interface UnlinkPhaseFromBacklogCategoryBody {
  projectId: string;
  phaseId: string;
  /** Required: identifies the specific link entry to remove. */
  backlogProjectKey: string;
  categoryId: number;
  removeImportedItems?: boolean;
}

export type UnlinkPhaseFromBacklogCategoryResult =
  | { ok: true; cleared: boolean; removedItemCount: number; clearedItemCount: number }
  | { ok: false; reason: 'phase-not-found' | 'not-linked'; message: string };

export interface SyncBacklogCategoryNowBody {
  projectId: string;
  phaseId: string;
  /**
   * Identify a single entry to sync. Omit BOTH to sync every entry on the
   * phase (sequentially, per-entry isolated errors).
   */
  backlogProjectKey?: string;
  categoryId?: number;
}

export interface PerCategoryEntrySyncOutcome {
  backlogProjectKey: string;
  categoryId: number;
  ok: boolean;
  added: string[];
  removed: string[];
  unchanged: string[];
  totalIssuesInCategory: number;
  truncated: boolean;
  reason?: 'backlog-error';
  message?: string;
}

export type SyncBacklogCategoryNowResult =
  | {
      ok: true;
      entries: PerCategoryEntrySyncOutcome[];
      added: string[];
      removed: string[];
      unchanged: string[];
      totalIssuesInCategory: number;
      truncated: boolean;
    }
  | {
      ok: false;
      reason: 'phase-not-found' | 'not-linked' | 'backlog-error';
      message: string;
    };

export type CategoryLinkRunner = (
  body: LinkPhaseToBacklogCategoryBody,
) => Promise<LinkPhaseToBacklogCategoryResult>;
export type CategoryUnlinkRunner = (
  body: UnlinkPhaseFromBacklogCategoryBody,
) => Promise<UnlinkPhaseFromBacklogCategoryResult>;
export type CategorySyncNowRunner = (
  body: SyncBacklogCategoryNowBody,
) => Promise<SyncBacklogCategoryNowResult>;

let _linkRunner: CategoryLinkRunner | null = null;
let _unlinkRunner: CategoryUnlinkRunner | null = null;
let _syncRunner: CategorySyncNowRunner | null = null;

export function setCategoryLinkRunnerForTests(
  runner: CategoryLinkRunner | null,
): CategoryLinkRunner | null {
  const prev = _linkRunner;
  _linkRunner = runner;
  return prev;
}
export function setCategoryUnlinkRunnerForTests(
  runner: CategoryUnlinkRunner | null,
): CategoryUnlinkRunner | null {
  const prev = _unlinkRunner;
  _unlinkRunner = runner;
  return prev;
}
export function setCategorySyncNowRunnerForTests(
  runner: CategorySyncNowRunner | null,
): CategorySyncNowRunner | null {
  const prev = _syncRunner;
  _syncRunner = runner;
  return prev;
}

function defaultLinkRunner(): CategoryLinkRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      LinkPhaseToBacklogCategoryBody,
      LinkPhaseToBacklogCategoryResult
    >(fns, 'linkPhaseToBacklogCategory');
    const res = await callable(body);
    return res.data;
  };
}

function defaultUnlinkRunner(): CategoryUnlinkRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      UnlinkPhaseFromBacklogCategoryBody,
      UnlinkPhaseFromBacklogCategoryResult
    >(fns, 'unlinkPhaseFromBacklogCategory');
    const res = await callable(body);
    return res.data;
  };
}

function defaultSyncNowRunner(): CategorySyncNowRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<
      SyncBacklogCategoryNowBody,
      SyncBacklogCategoryNowResult
    >(fns, 'syncBacklogCategoryNow');
    const res = await callable(body);
    return res.data;
  };
}

export async function linkPhaseToBacklogCategory(
  body: LinkPhaseToBacklogCategoryBody,
): Promise<LinkPhaseToBacklogCategoryResult> {
  const run = _linkRunner ?? defaultLinkRunner();
  return await run(body);
}

export async function unlinkPhaseFromBacklogCategory(
  body: UnlinkPhaseFromBacklogCategoryBody,
): Promise<UnlinkPhaseFromBacklogCategoryResult> {
  const run = _unlinkRunner ?? defaultUnlinkRunner();
  return await run(body);
}

export async function syncBacklogCategoryNow(
  body: SyncBacklogCategoryNowBody,
): Promise<SyncBacklogCategoryNowResult> {
  const run = _syncRunner ?? defaultSyncNowRunner();
  return await run(body);
}
